import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';

import { Box, Button, Page, TextButton } from '@zillow/constellation-10';

import { RentalManagerFeatureLaunchNotificationPage } from '@zg-rentals/ts-rental-manager-api-client';
import {
  useRentalManagerUserClaimableListingsWebApiHandler_getClaimableProperties,
  useRentalManagerUserClaimableListingsWebApiHandler_userHasNewPropertiesToClaim,
} from '@zg-rentals/ts-rental-manager-api-client-react-query';
import {
  emitAddFeedClickEvent,
  emitAddPropertyClickEvent,
  emitOpenEvent,
} from '@zg-rentals/zillow-rental-manager/analytics/events/addPropertyModal';

import { useGetPropertiesData, useSpotlightOverview } from '../../hooks/queries/properties/zram';
import { useScreenSize } from '../../hooks/useScreenSize';
import accessUtils from '../../utils/accessUtils';
import {
  initializeFiltersFromRouter,
  isRedirectToPayments,
  removeQueryParams,
  renderInitialDisplayModal,
  updateRouterWithFilters,
} from '../../utils/propertiesPageUtils';

import { CLAIM_YOUR_LISTING_COOKIES, SPOTLIGHT_LISTINGS_COOKIES } from '../../constants/CookieConstants';
import PropertiesConstants from '../../constants/PropertiesConstants';
import QueryConstants from '../../constants/QueryConstants';
import { EXTERNAL_ROUTES, ROUTES } from '../../constants/urls';

import { useAuth } from '../../components/AuthProvider';
import AddFeedListingsModal from '../../components/modals/AddFeedListingsModal';
import AddPropertyModal from '../../components/modals/AddPropertyModal';
import ClaimPropertiesModal from '../../components/modals/ClaimPropertiesModal';
import SpotlightListingsFirstUseModal from '../../components/modals/SpotlightListingsFirstUseModal';
import PostClaimingModal from '../../components/PostClaimingModal';
import EmailVerification from '../../components/properties/EmailVerification';
import NotificationBanner from '../../components/properties/NotificationBanner';
import { PropertiesPageNotifications } from '../../components/properties/PropertiesPageNotifications';

import {
  emitViewPropertiesPageEvent,
  EVENT_IDS as PROPERTIES_PAGE_EVENT_IDS,
} from '../../analytics/events/propertiesPage';
import AuthCheckWrapper from '../AuthCheckWrapper';
import AutoSpotlightPanel from './AutoSpotlightPanel';
import { AutoSpotlightProvider } from './AutoSpotlightPanel/AutoSpotlightContext';
import ClaimPathBanners from './ClaimPathBanners';
import { Filters } from './Filters';
import MediaCreditsCheckoutHelper from './MediaCreditsCheckoutHelper';
import { PageHeading } from './PageHeading';
import { PropertyTable } from './PropertyTable';
import SpotlightListingsBanner from './SpolightListingsBanner';

import type { SetStateAction } from 'react';
import type { ZrmClickstreamUser } from '@zg-rentals/clickstream';
import type { FilterState } from '../../utils/propertiesPageUtils';

const defaultFilters = {
  searchText: PropertiesConstants.PROPERTIES_SEARCHTEXT_DEFAULT,
  subNavFilterType: PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.ALL,
};

export const ZRAMPropertiesPage = () => {
  const router = useRouter();
  const { user } = useAuth();
  const { isScreenMobile } = useScreenSize();

  const isCheckInitialDisplayModal = useRef(true);

  const [filters, setFilters] = useState<FilterState>(() => initializeFiltersFromRouter(router.query, defaultFilters));
  const [isValidatingFilters, setIsValidatingFilters] = useState(true);
  const [showAddFeedListingsModal, setShowAddFeedListingsModal] = useState(false);
  const [showClaimPropertiesModal, setShowClaimPropertiesModal] = useState({
    isOpen: false,
    isClaimMoreModal: false,
  });
  const [showPostClaimingModal, setShowPostClaimingModal] = useState(false);
  const [showSpotlightListingsFirstUseModal, setShowSpotlightListingsFirstUseModal] = useState(false);
  const [showAddPropertyModal, setShowAddPropertyModal] = useState(false);

  const isNonAdvertiser = user ? accessUtils.isNonAdvertiser({ user }) : false;

  const {
    data: propertiesData,
    isPending: isPropertiesPending,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useGetPropertiesData(filters);

  const { data: spotlightOverviewData, isPending: isSpotlightOverviewPending } = useSpotlightOverview();

  const {
    total: numTotalListings = 0,
    freeClaimedListingCount: numClaimedListings = 0,
    archived: numArchivedListings = 0,
    freeBonusListingCount: numBonusListings = 0,
    atRisk: numAtRiskListings = 0,
  } = propertiesData?.listingCount || {};

  const { data: { newPropertiesCount = 0, hasNewProperties: hasNewPropertiesToClaim = false } = {} } =
    useRentalManagerUserClaimableListingsWebApiHandler_userHasNewPropertiesToClaim({
      enabled: user?.features?.canClaimFreeListings ?? false,
    });
  const { data: { unitProperties: claimableProperties = [] } = {}, isFetching: isClaimablePropertiesFetching } =
    useRentalManagerUserClaimableListingsWebApiHandler_getClaimableProperties(
      {},
      {
        enabled: user?.features?.canClaimFreeListings ?? false,
      },
    );

  const canAccessClaimPathModal = accessUtils.canAccessClaimPath({
    isScreenMobile,
    hasAnyPropertiesToClaim: claimableProperties.length > 0,
    cookie: CLAIM_YOUR_LISTING_COOKIES.HAS_CLOSED_MODAL,
    isNonAdvertiser,
  });

  const canAccessClaimPathMorePropertiesModal = accessUtils.canAccessClaimMorePath({
    isScreenMobile,
    hasAnyClaimedListings: numClaimedListings > 0,
    hasNewPropertiesToClaim,
    cookie: CLAIM_YOUR_LISTING_COOKIES.HAS_CLOSED_CLAIM_MORE_MODAL,
  });

  const canAccessSpotlightListingsFirstUseModal = accessUtils.canAccessSpotlightListingsFirstUseModal({
    isScreenMobile,
    isSpotlightListingsEligible: Boolean(spotlightOverviewData?.isEligible),
    cookie: SPOTLIGHT_LISTINGS_COOKIES.HAS_CLOSED_SPOTLIGHT_LISTINGS_MODAL,
  });

  const queryParams = router.query;

  const isArchivedView = filters.subNavFilterType === PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.ARCHIVED;
  // Access comes from the cookie override or an allowlisted email, but only LS-eligible customers see the Auto
  // Spotlight UI; others keep the existing package-only display and the contact-sales upsell. Non-LS customers can't
  // pin/exclude, so the control is hidden for them entirely.
  const showAutoSpotlightUi = accessUtils.canAccessAutoSpotlightUi({ user, spotlightOverview: spotlightOverviewData });
  // Smart Assign ships as its own panel, but requires auto-spotlight access too (both cookies) so the table's
  // row controls stay wired — the smart-assign cookie alone would render a half-working panel.
  const showSmartAssignUi = accessUtils.canAccessSmartAssignPanel({ user, spotlightOverview: spotlightOverviewData });
  const showSpotlightUi = showAutoSpotlightUi || showSmartAssignUi;

  useEffect(() => {
    // Scroll to top of page on initial load and when switching between views
    window.scrollTo(0, 0);

    // Clear search text when switching to archived view
    if (isArchivedView) {
      setFilters({ ...filters, searchText: '' });
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- preserving existing behavior; not re-verified
  }, [isArchivedView]);

  useEffect(() => {
    emitViewPropertiesPageEvent({ user });
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- preserving existing behavior; not re-verified
  }, []);

  useEffect(() => {
    // Once data has loaded, validate if user can access the requested tab
    if (!isPropertiesPending && !isClaimablePropertiesFetching && !isSpotlightOverviewPending) {
      const bypassOlympusRedirect = queryParams.bypassOlympusRedirect === 'true';
      if (isNonAdvertiser && numTotalListings === 0 && !bypassOlympusRedirect) {
        window.location.href = EXTERNAL_ROUTES.OLYMPUS_REDIRECT;
      }

      const validatedFilters = validateFiltersAccess(initializeFiltersFromRouter(router.query, filters));
      setFilters(validatedFilters);

      if (validatedFilters.subNavFilterType !== filters.subNavFilterType) {
        updateRouterWithFilters(router, validatedFilters);
      }

      setIsValidatingFilters(false);
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- preserving existing behavior; not re-verified
  }, [router.asPath, isPropertiesPending, isClaimablePropertiesFetching, isSpotlightOverviewPending]);

  // Check for initial modals once all data has loaded
  useEffect(() => {
    if (isCheckInitialDisplayModal.current && !isPropertiesPending && !isSpotlightOverviewPending) {
      isCheckInitialDisplayModal.current = false;
      checkInitialDisplayModal();
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- preserving existing behavior; not re-verified
  }, [isPropertiesPending, isSpotlightOverviewPending]);

  // Re-check for modals when specific query params change (e.g., from left nav)
  useEffect(() => {
    const postingPath = router.query[QueryConstants.POSTING_PATH];
    const addAFeed = router.query[QueryConstants.ADD_A_FEED];

    if (postingPath || addAFeed) {
      checkInitialDisplayModal();
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- preserving existing behavior; not re-verified
  }, [router.query[QueryConstants.POSTING_PATH], router.query[QueryConstants.ADD_A_FEED]]);

  const updateFilters = (newFilters: SetStateAction<FilterState>) => {
    setFilters((current) => {
      const next = typeof newFilters === 'function' ? newFilters(current) : newFilters;
      updateRouterWithFilters(router, next);
      return next;
    });
  };

  const validateFiltersAccess = (filters: FilterState): FilterState => {
    const { subNavFilterType } = filters;
    const canAccessClaimed =
      numClaimedListings > 0 ||
      accessUtils.canAccessClaimPath({
        isScreenMobile,
        hasAnyPropertiesToClaim: claimableProperties.length > 0,
        isNonAdvertiser: user ? accessUtils.isNonAdvertiser({ user }) : false,
      });
    const canAccessSpotlightFilter = accessUtils.canAccessListingSpotlightFilter({
      user,
      spotlightOverview: spotlightOverviewData,
    });

    if (
      (subNavFilterType === PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.CLAIMED && !canAccessClaimed) ||
      (subNavFilterType === PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.AT_RISK && numAtRiskListings === 0) ||
      (subNavFilterType === PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.FREE_BONUS_LISTINGS &&
        numBonusListings === 0) ||
      (subNavFilterType === PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.SPOTLIGHT && !canAccessSpotlightFilter) ||
      !Object.values(PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE).includes(subNavFilterType)
    ) {
      return {
        ...filters,
        subNavFilterType: PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.ALL,
      };
    }
    return filters;
  };

  const checkInitialDisplayModal = () => {
    renderInitialDisplayModal({
      router,
      totalListingsWithoutFilters: numTotalListings,
      showAddPropertyModal,
      setShowAddPropertyModal: handleAddPropertyClick,
      canAccessUserOnboardingNewUser: false,
      setShowAddFeedListingsModal: () => {
        if (user?.restrictions?.isAllowed && accessUtils.canAccessAddFeedListingsModal({ user })) {
          setShowAddFeedListingsModal(true);
        }
      },
      setShowPostClaimingModal,
      setShowClaimPropertiesModal,
      canAccessClaimPathModal,
      canAccessClaimPathMorePropertiesModal,
      setShowSpotlightListingsFirstUseModal,
      canAccessSpotlightListingsFirstUseModal,
    });
  };

  const handleAddPropertyClick = ({ shouldSendAnalytics = false }: { shouldSendAnalytics?: boolean }) => {
    const shouldRedirectToPayments = isRedirectToPayments(router.query);

    if (user?.features?.canAddListing && user?.restrictions?.isAllowed) {
      setShowAddPropertyModal(true);
    }

    if (shouldSendAnalytics) {
      emitOpenEvent({
        user: user as ZrmClickstreamUser,
        triggerObject: shouldRedirectToPayments ? 'start_collecting_rent_button' : undefined,
      });

      emitAddPropertyClickEvent({ user: user as ZrmClickstreamUser });
    }
  };

  const handleAddFeedListingsClick = () => {
    setShowAddFeedListingsModal(true);
    emitAddFeedClickEvent({ user: user as ZrmClickstreamUser });
  };

  const handleViewArchivedPropertiesClick = () => {
    router.push({
      pathname: ROUTES.PROPERTIES,
      query: {
        [QueryConstants.SUB_NAV_FILTER_TYPE]: PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.ARCHIVED,
      },
    });
  };

  return (
    <AuthCheckWrapper
      loading={<PropertyTable filters={filters} setFilters={updateFilters} isSkeletonView={isValidatingFilters} />}
      redirecting={<PropertyTable filters={filters} setFilters={updateFilters} isSkeletonView={isValidatingFilters} />}
    >
      <Page.Root
        data-testid="ZRAMPropertiesPage"
        data-eventid={PROPERTIES_PAGE_EVENT_IDS.VIEW_PROPERTIES_PAGE}
        data-eventdescription="View properties page"
        css={{ backgroundColor: 'bg.screen.neutral' }}
      >
        <Page.Header
          css={{
            padding: '0 !important',
            margin: '0 !important',
            maxWidth: 'none',
            borderBottom: '1px solid {colors.border.muted}',
          }}
        >
          <PageHeading
            handleAddPropertyClick={handleAddPropertyClick}
            handleAddFeedListingsClick={handleAddFeedListingsClick}
            isArchivedView={isArchivedView}
          />
        </Page.Header>
        <Page.Content css={{ padding: { base: '0 {spacing.600}', xl: '0 {spacing.1200}' }, maxWidth: '100%' }}>
          {/* Banners */}
          {user?.features?.canAccessFeatureLaunchNotifications && (
            <NotificationBanner page={RentalManagerFeatureLaunchNotificationPage.PROPERTIES} />
          )}
          <PropertiesPageNotifications />
          <EmailVerification />
          <ClaimPathBanners
            hasAnyClaimedListings={numClaimedListings > 0}
            subNavFilterType={filters.subNavFilterType}
          />
          {/* Table */}
          {isArchivedView ? null : (
            <Filters filters={filters} setFilters={updateFilters} actions={<MediaCreditsCheckoutHelper />} />
          )}
          {showSpotlightUi ? (
            <AutoSpotlightProvider listings={propertiesData?.listings ?? []} active={!isArchivedView}>
              {/* Smart Assign's header owns the placement summary, so its legacy upsell banner is redundant. */}
              {spotlightOverviewData?.isEligible &&
                !isArchivedView &&
                !isSpotlightOverviewPending &&
                !showSmartAssignUi && (
                  <SpotlightListingsBanner
                    hasSpotlightedListings={spotlightOverviewData?.hasSpotlightedListings ?? false}
                    availableCount={spotlightOverviewData?.availableCount}
                  />
                )}
              {!isArchivedView && <AutoSpotlightPanel showSmartAssign={showSmartAssignUi} />}
              <PropertyTable
                filters={filters}
                setFilters={updateFilters}
                isSkeletonView={isValidatingFilters}
                showAutoSpotlightControl={showAutoSpotlightUi && !isArchivedView}
                showSmartAssignControl={showSmartAssignUi && !isArchivedView}
              />
            </AutoSpotlightProvider>
          ) : (
            <>
              {spotlightOverviewData?.isEligible && !isArchivedView && !isSpotlightOverviewPending && (
                <SpotlightListingsBanner
                  hasSpotlightedListings={spotlightOverviewData?.hasSpotlightedListings ?? false}
                  availableCount={spotlightOverviewData?.availableCount}
                />
              )}
              <PropertyTable filters={filters} setFilters={updateFilters} isSkeletonView={isValidatingFilters} />
            </>
          )}
          {/* CTAs */}
          {numArchivedListings > 0 && !isArchivedView && (
            <TextButton onClick={handleViewArchivedPropertiesClick} css={{ marginTop: '600' }}>
              View archived properties
            </TextButton>
          )}
          {hasNextPage && (
            <Box css={{ display: 'flex', justifyContent: 'center', marginTop: '600' }}>
              <Button tone="brand" emphasis="filled" onClick={() => fetchNextPage()} loading={isFetchingNextPage}>
                Load more properties
              </Button>
            </Box>
          )}
        </Page.Content>
        {showAddFeedListingsModal && (
          <AddFeedListingsModal
            onClose={() => {
              removeQueryParams(router, [QueryConstants.ADD_A_FEED]);
              setShowAddFeedListingsModal(false);
            }}
          />
        )}
        {showAddPropertyModal && (
          <AddPropertyModal
            isOpen={showAddPropertyModal}
            onClose={() => {
              removeQueryParams(router, [QueryConstants.POSTING_PATH, QueryConstants.REDIRECT_TO]);
              setShowAddPropertyModal(false);
            }}
          />
        )}
        {!isScreenMobile && showClaimPropertiesModal.isOpen && (
          <ClaimPropertiesModal
            onClose={() => {
              setShowClaimPropertiesModal({ ...showClaimPropertiesModal, isOpen: false });
            }}
            isClaimMoreModal={showClaimPropertiesModal.isClaimMoreModal}
            newPropertiesCount={newPropertiesCount}
          />
        )}
        {showPostClaimingModal && (
          <PostClaimingModal
            onClose={() => {
              removeQueryParams(router, [QueryConstants.CLAIM_DASHBOARD]);
              setShowPostClaimingModal(false);
            }}
          />
        )}
        {showSpotlightListingsFirstUseModal && (
          <SpotlightListingsFirstUseModal onClose={() => setShowSpotlightListingsFirstUseModal(false)} />
        )}
      </Page.Root>
    </AuthCheckWrapper>
  );
};
